import React, { createContext, useContext, useEffect, useState } from 'react';

export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'unknown';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface LocationContextType {
  permissionStatus: PermissionStatus;
  coordinates: Coordinates | null;
  error: string | null;
}

const LocationContext = createContext<LocationContextType>({
  permissionStatus: 'unknown',
  coordinates: null,
  error: null,
});

export const useLocation = () => useContext(LocationContext);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [permissionStatus, setPermissionStatus] =
    useState<PermissionStatus>('unknown');
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocalización no soportada por el navegador.');
      setPermissionStatus('denied');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        setCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setPermissionStatus('granted');
      },
      err => {
        setError(`Error obteniendo ubicación: ${err.message}`);
        setPermissionStatus('denied');
      }
    );
  };

  useEffect(() => {
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(status => {
          setPermissionStatus(status.state as PermissionStatus);

          if (status.state !== 'denied') {
            requestLocation();
          }

          status.onchange = () => {
            const newStatus = status.state as PermissionStatus;
            setPermissionStatus(newStatus);
            if (newStatus === 'granted') {
              requestLocation();
            }
          };
        })
        .catch(() => {
          // fallback si no se puede consultar permisos
          requestLocation();
        });
    } else {
      // fallback en navegadores antiguos
      requestLocation();
    }
  }, []);

  return (
    <LocationContext.Provider value={{ permissionStatus, coordinates, error }}>
      {children}
    </LocationContext.Provider>
  );
};
